import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { HERO_ALT, CHALLENGE_DOMAINS } from "@/lib/content";

const DOMAINS = CHALLENGE_DOMAINS;
const OUTCOMES = [
  "Research question", "Feasibility evidence", "Technical architecture", "Prototype",
  "Software / tool", "Intellectual property", "Licensing / commercialisation pathway", "Other",
];
const STEPS = ["Organisation", "Challenge", "Context", "Desired outcome", "Review & consent"];
const DRAFT_KEY = "dbrf_challenge_draft";

const EMPTY = {
  full_name: "", work_email: "", role: "", organization: "", website: "", country: "",
  title: "", problem: "", why_now: "", tried: "", blocked: "",
  domains: [], systems: "", constraints: "", regulatory: "", existing_team: "",
  desired_outcomes: [], timing: "", budget_range: "", success_evidence: "",
  accuracy_confirmed: false, privacy_consent: false, no_confidential_ack: false, company: "",
};

function Field({ label, name, value, onChange, type = "text", required, textarea, placeholder }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-navy">{label}{required && <span className="text-[var(--error)]"> *</span>}</span>
      <Comp
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`challenge-${name}`}
        placeholder={placeholder}
        rows={textarea ? 4 : undefined}
        className="w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]"
      />
    </label>
  );
}

function TagPicker({ options, selected, onToggle, name }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`challenge-${name}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`btn-transition rounded-full border px-3 py-1.5 text-xs ${active ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] bg-[var(--paper)] text-navy hover:border-[var(--cobalt)]"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function BringAChallenge() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") }; } catch { return EMPTY; }
  });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form]);

  const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const toggle = (name, value) => setForm((f) => ({ ...f, [name]: f[name].includes(value) ? f[name].filter((x) => x !== value) : [...f[name], value] }));

  const validateStep = () => {
    const e = [];
    if (step === 0) {
      if (!form.full_name) e.push("Full name is required");
      if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.work_email)) e.push("A valid work email is required");
      if (!form.role) e.push("Role is required");
      if (!form.organization) e.push("Organisation is required");
      if (!form.country) e.push("Country is required");
    }
    if (step === 1) {
      if (!form.title) e.push("A short title is required");
      if (!form.problem) e.push("A problem description is required");
      if (!form.why_now) e.push("Please describe why it matters now");
    }
    if (step === 4) {
      if (!form.accuracy_confirmed) e.push("Please confirm accuracy");
      if (!form.privacy_consent) e.push("Privacy consent is required");
      if (!form.no_confidential_ack) e.push("Please acknowledge no confidential information was included");
    }
    setErrors(e);
    return e.length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => { setErrors([]); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/challenge-submissions", form);
      setResult(data);
      localStorage.removeItem(DRAFT_KEY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <>
        <PageMeta title="Challenge received" />
        <section className="container-dbrf py-24 lg:py-32">
          <div className="mx-auto max-w-xl rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-10 text-center" data-testid="challenge-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--success)]/15 text-[var(--success)]"><Check size={26} /></div>
            <h1 className="mt-6 font-display text-3xl font-bold text-navy">Brief received</h1>
            <p className="mt-3 text-[var(--mineral)]">DBRF will review the brief to determine whether the problem is a fit and which capabilities it may require.</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--ivory)] px-4 py-2">
              <span className="font-mono text-sm text-navy" data-testid="challenge-reference">{result.reference}</span>
              <button onClick={() => { navigator.clipboard?.writeText(result.reference); toast.success("Reference copied"); }} aria-label="Copy reference" className="text-[var(--cobalt)]"><Copy size={14} /></button>
            </div>
            <div className="mt-8"><Link to="/" className="text-sm font-semibold text-[var(--cobalt)] link-underline">Return home</Link></div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Bring us a challenge" description="Start with the problem, not a predetermined solution." />
      <PageHero
        eyebrow="Bring us a challenge"
        title="Start with the problem, not a predetermined solution."
        intro="Share enough context for DBRF to assess fit. Do not submit confidential information, personal data or protected intellectual property through this initial form. A protected discussion can be arranged when appropriate."
        image="/assets/08-bring-a-challenge-hero.webp"
        alt={HERO_ALT["bring-a-challenge"]}
        objectPosition="center 40%"
      />

      <section className="container-dbrf py-14 lg:py-20">
        <div className="mx-auto max-w-3xl">
          {/* progress */}
          <div className="mb-10">
            <div className="mb-3 flex justify-between">
              {STEPS.map((s, i) => (
                <span key={s} className={`font-mono text-[0.66rem] uppercase tracking-wide ${i <= step ? "text-[var(--cobalt)]" : "text-[var(--mineral)]"}`}>{i + 1}. {s}</span>
              ))}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--line)]">
              <div className="h-full rounded-full bg-[var(--cobalt)] transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} data-testid="challenge-progress" />
            </div>
          </div>

          {errors.length > 0 && (
            <div role="alert" className="mb-6 rounded-xl border border-[var(--error)]/30 bg-[var(--error)]/10 p-4" data-testid="challenge-error-summary">
              <ul className="list-inside list-disc text-sm text-[var(--error)]">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
            </div>
          )}

          {/* honeypot */}
          <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)} name="company" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

          <form onSubmit={(e) => e.preventDefault()} noValidate className="rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-8 lg:p-10">
            {step === 0 && (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full name" name="full_name" value={form.full_name} onChange={set} required />
                <Field label="Work email" name="work_email" type="email" value={form.work_email} onChange={set} required />
                <Field label="Role" name="role" value={form.role} onChange={set} required />
                <Field label="Organisation" name="organization" value={form.organization} onChange={set} required />
                <Field label="Website" name="website" value={form.website} onChange={set} placeholder="https://" />
                <Field label="Country" name="country" value={form.country} onChange={set} required />
              </div>
            )}
            {step === 1 && (
              <div className="grid gap-5">
                <Field label="Short title" name="title" value={form.title} onChange={set} required />
                <Field label="Describe the problem" name="problem" value={form.problem} onChange={set} textarea required />
                <Field label="Why it matters now" name="why_now" value={form.why_now} onChange={set} textarea required />
                <Field label="What has already been tried" name="tried" value={form.tried} onChange={set} textarea />
                <Field label="Where the current approach is blocked" name="blocked" value={form.blocked} onChange={set} textarea />
              </div>
            )}
            {step === 2 && (
              <div className="grid gap-5">
                <div><span className="mb-2 block text-sm font-medium text-navy">Relevant domain(s)</span><TagPicker options={DOMAINS} selected={form.domains} onToggle={(v) => toggle("domains", v)} name="domain" /></div>
                <Field label="Data or physical systems involved" name="systems" value={form.systems} onChange={set} textarea />
                <Field label="Known constraints" name="constraints" value={form.constraints} onChange={set} textarea />
                <Field label="Regulatory or safety considerations" name="regulatory" value={form.regulatory} onChange={set} textarea />
                <Field label="Existing team / capability" name="existing_team" value={form.existing_team} onChange={set} textarea />
              </div>
            )}
            {step === 3 && (
              <div className="grid gap-5">
                <div><span className="mb-2 block text-sm font-medium text-navy">What outcome would be meaningful?</span><TagPicker options={OUTCOMES} selected={form.desired_outcomes} onToggle={(v) => toggle("desired_outcomes", v)} name="outcome" /></div>
                <Field label="Target timing" name="timing" value={form.timing} onChange={set} placeholder="e.g. within 6 months" />
                <Field label="What evidence would make this project successful?" name="success_evidence" value={form.success_evidence} onChange={set} textarea placeholder="Examples: quality-equivalent reduction, technical feasibility, validated prototype, latency or memory improvement, user-understanding measure, documented security boundary or commercial pilot decision." />
                <p className="text-sm text-[var(--mineral)]">Budget range is discussed later and is not required here.</p>
              </div>
            )}
            {step === 4 && (
              <div className="grid gap-5">
                <div className="rounded-xl border border-[var(--line)] bg-[var(--ivory)] p-5">
                  <p className="eyebrow mb-3">Summary</p>
                  <dl className="grid gap-2 text-sm sm:grid-cols-2">
                    <div><dt className="text-[var(--mineral)]">Organisation</dt><dd className="text-navy">{form.organization || "—"}</dd></div>
                    <div><dt className="text-[var(--mineral)]">Challenge</dt><dd className="text-navy">{form.title || "—"}</dd></div>
                    <div><dt className="text-[var(--mineral)]">Domains</dt><dd className="text-navy">{form.domains.join(", ") || "—"}</dd></div>
                    <div><dt className="text-[var(--mineral)]">Desired outcomes</dt><dd className="text-navy">{form.desired_outcomes.join(", ") || "—"}</dd></div>
                  </dl>
                </div>
                {[
                  ["accuracy_confirmed", "I confirm the information above is accurate to the best of my knowledge."],
                  ["privacy_consent", "I consent to DBRF processing this submission to assess fit."],
                  ["no_confidential_ack", "I have not included confidential or restricted information."],
                ].map(([name, label]) => (
                  <label key={name} className="flex items-start gap-3">
                    <input type="checkbox" checked={form[name]} onChange={(e) => set(name, e.target.checked)} data-testid={`challenge-${name}`} className="mt-1 h-4 w-4 accent-[var(--cobalt)]" />
                    <span className="text-sm text-navy">{label}</span>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <button type="button" onClick={back} disabled={step === 0} data-testid="challenge-back" className="btn-transition inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-5 py-2.5 text-sm font-semibold text-navy disabled:opacity-40">
                <ArrowLeft size={15} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={next} data-testid="challenge-next" className="btn-transition inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[var(--cobalt)]">
                  Continue <ArrowRight size={15} />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={submitting} data-testid="challenge-submit" className="btn-transition inline-flex items-center gap-2 rounded-full bg-[var(--amber)] px-6 py-2.5 text-sm font-semibold text-navy disabled:opacity-60">
                  {submitting ? "Submitting…" : "Submit brief"} <Check size={15} />
                </button>
              )}
            </div>
          </form>
          <p className="mt-4 text-center text-xs text-[var(--mineral)]">Your progress is saved in this browser until you submit.</p>
        </div>
      </section>
    </>
  );
}
